import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { gamesAPI } from '../../services/api';
import { useProfile } from '../../context/ProfileContext';

const WORDS = [
  { word: 'CAT',   hint: '🐱' },
  { word: 'DOG',   hint: '🐶' },
  { word: 'SUN',   hint: '☀️' },
  { word: 'STAR',  hint: '⭐' },
  { word: 'MOON',  hint: '🌙' },
  { word: 'FISH',  hint: '🐟' },
  { word: 'BIRD',  hint: '🐦' },
  { word: 'TREE',  hint: '🌳' },
  { word: 'CAKE',  hint: '🎂' },
  { word: 'FROG',  hint: '🐸' },
  { word: 'LION',  hint: '🦁' },
  { word: 'BEAR',  hint: '🐻' },
  { word: 'RAIN',  hint: '🌧️' },
  { word: 'BOOK',  hint: '📚' },
  { word: 'BALL',  hint: '⚽' },
];

function scramble(word) {
  const arr = word.split('');
  do {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  } while (arr.join('') === word);
  return arr;
}

export default function WordScrambleScreen() {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useProfile();

  const [words] = useState(() => [...WORDS].sort(() => Math.random() - 0.5).slice(0, 6));
  const [current, setCurrent] = useState(0);
  const [letters, setLetters] = useState(() => scramble(WORDS[0]?.word || 'CAT'));
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(null);

  const q = words[current];

  const initRound = useCallback((idx) => {
    setLetters(scramble(words[idx].word));
    setSelected([]);
    setResult(null);
  }, [words]);

  const pickLetter = (i) => {
    if (result || selected.includes(i)) return;
    const newSel = [...selected, i];
    setSelected(newSel);
    if (newSel.length === q.word.length) {
      const formed = newSel.map(idx => letters[idx]).join('');
      const correct = formed === q.word;
      setResult(correct ? 'correct' : 'wrong');
      if (correct) setScore(s => s + 100);
      setTimeout(() => {
        if (!correct) { setSelected([]); setResult(null); return; }
        if (current + 1 >= words.length) {
          setGameOver(true);
          saveResult(score + 100);
        } else {
          const next = current + 1;
          setCurrent(next);
          initRound(next);
        }
      }, 1000);
    }
  };

  const removeLetter = (selIdx) => {
    if (result) return;
    setSelected(prev => prev.filter((_, i) => i !== selIdx));
  };

  const saveResult = async (finalScore) => {
    if (!profile) return;
    try {
      const res = await gamesAPI.saveResult({ profileId: profile.id, gameId: 'word-scramble', score: finalScore });
      setPointsEarned(res.pointsEarned);
      refreshProfile();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="screen">
      <div className="screen-header" style={{ background: 'linear-gradient(135deg, #96CEB4, #55A88A)', paddingBottom: 20 }}>
        <button className="btn-back" onClick={() => navigate('/games')} style={{ background: 'rgba(255,255,255,0.25)', color: 'white' }}>←</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: 'var(--font-title)', color: 'white', fontSize: '1.4rem' }}>📝 Word Scramble</h1>
          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>{current + 1}/{words.length} · Score: {score}</div>
        </div>
      </div>

      <div className="screen-body" style={{ paddingTop: 30 }}>
        {gameOver ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '5rem', marginBottom: 12 }}>🏆</div>
            <h2 style={{ fontFamily: 'var(--font-title)', color: 'var(--purple)', fontSize: '1.8rem' }}>Amazing!</h2>
            <p style={{ color: 'var(--gray)', margin: '10px 0 20px' }}>Score: <strong>{score}</strong> out of {words.length * 100}</p>
            {pointsEarned && (
              <div style={{ background: '#FFF9E6', border: '2px solid var(--yellow)', borderRadius: 14, padding: 12, marginBottom: 20 }}>
                <strong>⭐ +{pointsEarned} points earned!</strong>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => window.location.reload()}>Play Again</button>
              <button className="btn" style={{ flex: 1, background: 'var(--bg)' }} onClick={() => navigate('/games')}>Games</button>
            </div>
          </div>
        ) : (
          <>
            <div className="progress-bar" style={{ marginBottom: 24 }}>
              <div className="progress-bar-fill" style={{ width: `${(current / words.length) * 100}%` }} />
            </div>

            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: '5rem', marginBottom: 8 }}>{q.hint}</div>
              <p style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Unscramble the letters to spell the word!</p>
              <p style={{ color: 'var(--gray)', fontSize: '0.8rem' }}>{q.word.length} letters</p>
            </div>

            {/* Answer slots */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 24 }}>
              {Array.from({ length: q.word.length }).map((_, i) => (
                <div key={i} onClick={() => selected[i] !== undefined && removeLetter(i)} style={{ width: 50, height: 56, borderRadius: 14, border: `3px solid ${result === 'correct' ? 'var(--green)' : result === 'wrong' ? 'var(--red)' : selected[i] !== undefined ? 'var(--purple)' : 'var(--border)'}`, background: result === 'correct' ? '#E8F5E9' : result === 'wrong' ? '#FFEBEE' : selected[i] !== undefined ? '#F0EBFF' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-title)', fontSize: '1.4rem', fontWeight: 900, color: 'var(--purple)', cursor: selected[i] !== undefined ? 'pointer' : 'default', transition: 'all 0.2s' }}>
                  {selected[i] !== undefined ? letters[selected[i]] : ''}
                </div>
              ))}
            </div>

            {result && (
              <div style={{ textAlign: 'center', fontSize: '1.5rem', fontWeight: 800, marginBottom: 16, color: result === 'correct' ? 'var(--green)' : 'var(--red)' }}>
                {result === 'correct' ? '✅ Correct!' : '❌ Try again!'}
              </div>
            )}

            {/* Letter tiles */}
            <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 10 }}>
              {letters.map((letter, i) => (
                <button key={i} onClick={() => pickLetter(i)} disabled={selected.includes(i)} style={{ width: 56, height: 62, borderRadius: 14, border: 'none', background: selected.includes(i) ? '#E0E0E0' : 'linear-gradient(135deg, #667eea, #764ba2)', color: selected.includes(i) ? '#999' : 'white', fontFamily: 'var(--font-title)', fontSize: '1.5rem', cursor: selected.includes(i) ? 'default' : 'pointer', boxShadow: selected.includes(i) ? 'none' : '0 5px 0 #4a5eb5', transition: 'all 0.15s', transform: selected.includes(i) ? 'translateY(4px)' : 'none' }}>
                  {letter}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
